// Détail d'un bon de commande : envoi, réceptions, annulation, clôture.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { achatsAPI } from '../../services/api';
import { apiError, dateFr, dateHeureFr, fcfa, today } from '../../utils/format';
import { BC_STATUS } from './status';
import { tokens } from '../../theme';

const q3 = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 3 });

const BonCommande = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAchats, canCompta } = useAuth();
  const [bc, setBc] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [rec, setRec] = useState(null);
  const [motif, setMotif] = useState(null); // { action: 'annuler'|'cloturer', texte }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { achatsAPI.commande(id).then((r) => setBc(r.data)).catch((e) => setError(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, after) => {
    setBusy(true); setError('');
    try { const r = await fn(); setToast(r.data.message); after?.(); load(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  if (!bc) return <Layout title="Bon de commande">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const st = BC_STATUS[bc.statut];
  const ouvert = ['ENVOYE', 'PARTIEL'].includes(bc.statut);

  const openRec = () => setRec({
    date: today(), bl_fournisseur: '', commentaire: '', error: '',
    q: Object.fromEntries(bc.lignes.map((l) => [l.id, String(Math.max(0, Math.round((l.quantite - l.quantite_recue) * 1000) / 1000))])),
  });
  const saveRec = async () => {
    try {
      const r = await achatsAPI.receptionner(id, {
        date: rec.date, bl_fournisseur: rec.bl_fournisseur, commentaire: rec.commentaire,
        lignes: Object.entries(rec.q).map(([k, v]) => ({ bc_ligne_id: Number(k), quantite: v })),
      });
      setRec(null); setToast(r.data.message); load();
    } catch (e) { setRec({ ...rec, error: apiError(e) }); }
  };

  return (
    <Layout title={bc.numero ? `Bon de commande ${bc.numero}` : 'Bon de commande (brouillon)'} plain>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={st.label} color={st.color} />
        <Typography color="text.secondary" sx={{ mr: 'auto' }}>
          {bc.envoye_le ? `Émis le ${dateHeureFr(bc.envoye_le)} par ${bc.envoye_par_nom}` : `Créé par ${bc.cree_par}`}
        </Typography>
        <Button variant="outlined" onClick={() => window.open(`/achats/commandes/${id}/imprimer`, '_blank')}>Imprimer / PDF</Button>
        {canAchats && bc.statut === 'BROUILLON' && (
          <>
            <Button variant="outlined" onClick={() => navigate(`/achats/commandes/${id}/modifier`)}>Modifier</Button>
            <Button color="error" disabled={busy} onClick={() => window.confirm('Supprimer ce brouillon ?') && run(() => achatsAPI.deleteCommande(id), () => navigate('/achats'))}>Supprimer</Button>
            <Button variant="contained" disabled={busy}
              onClick={() => window.confirm('Émettre ce bon de commande ? Il recevra son numéro et ne pourra plus être modifié.') && run(() => achatsAPI.envoyer(id))}>
              Émettre le bon de commande
            </Button>
          </>
        )}
        {canAchats && ouvert && <Button variant="contained" onClick={openRec}>Réceptionner une livraison</Button>}
        {canAchats && bc.statut === 'ENVOYE' && <Button color="error" onClick={() => setMotif({ action: 'annuler', texte: '' })}>Annuler</Button>}
        {canAchats && bc.statut === 'PARTIEL' && <Button onClick={() => setMotif({ action: 'cloturer', texte: '' })}>Clôturer (abandonner le reliquat)</Button>}
        {canCompta && !['BROUILLON', 'ANNULE'].includes(bc.statut) && (
          <Button variant="outlined" onClick={() => navigate(`/compta/factures-fournisseurs?bc=${id}`)}>Saisir la facture fournisseur</Button>
        )}
      </Box>
      {bc.motif_annulation && <Alert severity="warning" sx={{ mb: 2 }}>{bc.statut === 'ANNULE' ? 'Annulé' : 'Clôturé'} : {bc.motif_annulation}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Fournisseur</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{bc.fournisseur}</Typography>
            <Typography variant="body2">{[bc.f_contact, bc.f_telephone, bc.f_email].filter(Boolean).join(' · ')}</Typography>
            <Typography variant="body2" color="text.secondary">{bc.f_adresse}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
            <Typography variant="body2">Commande du <b>{dateFr(bc.date_commande)}</b>{bc.date_livraison_prevue && <> · livraison souhaitée le <b>{dateFr(bc.date_livraison_prevue)}</b></>}</Typography>
            {bc.lieu_livraison && <Typography variant="body2">Lieu : {bc.lieu_livraison}</Typography>}
            {bc.conditions && <Typography variant="body2">Conditions : {bc.conditions}</Typography>}
            {bc.commentaire && <Typography variant="body2" color="text.secondary">Note interne : {bc.commentaire}</Typography>}
            {bc.total_facture > 0 && <Typography variant="body2" sx={{ mt: 1 }}>Facturé par le fournisseur : <b>{fcfa(bc.total_facture)} F</b></Typography>}
          </CardContent></Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Désignation</TableCell><TableCell align="right">Commandé</TableCell><TableCell align="right">Reçu</TableCell>
              <TableCell align="right">Reste</TableCell><TableCell align="right">Prix unitaire HT</TableCell><TableCell align="right">Montant HT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bc.lignes.map((l) => {
              const reste = Math.max(0, l.quantite - l.quantite_recue);
              return (
                <TableRow key={l.id}>
                  <TableCell>{l.designation}{l.intrant_id && <Chip size="small" label="stock" variant="outlined" sx={{ ml: 1, height: 20 }} />}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{q3(l.quantite)} {l.unite}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{q3(l.quantite_recue)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: reste > 0 && ouvert ? 'warning.main' : 'text.secondary' }}>{q3(reste)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(l.prix_unitaire)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(l.montant)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow><TableCell colSpan={5} align="right">Total HT</TableCell><TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(bc.total_ht)}</TableCell></TableRow>
            <TableRow><TableCell colSpan={5} align="right">TVA {bc.taux_tva} %</TableCell><TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(bc.total_tva)}</TableCell></TableRow>
            <TableRow><TableCell colSpan={5} align="right"><b>Total TTC</b></TableCell><TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700 }}>{fcfa(bc.total_ttc)}</TableCell></TableRow>
          </TableBody>
        </Table>
      </Card>

      {bc.receptions.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Réceptions</Typography>
            {bc.receptions.map((r) => (
              <Box key={r.id} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {dateFr(r.date)}{r.bl_fournisseur ? ` — BL fournisseur ${r.bl_fournisseur}` : ''} <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>reçu par {r.recu_par}</Typography>
                </Typography>
                <Typography variant="body2">{(r.lignes || []).map((l) => `${l.designation} : ${q3(l.quantite)} ${l.unite}`).join(' · ')}</Typography>
                {r.commentaire && <Typography variant="body2" color="text.secondary">{r.commentaire}</Typography>}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!rec} onClose={() => setRec(null)} fullWidth maxWidth="sm">
        {rec && (
          <>
            <DialogTitle>Réception de la livraison</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {rec.error && <Alert severity="error" sx={{ mb: 2 }}>{rec.error}</Alert>}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField type="date" size="small" label="Date de réception" InputLabelProps={{ shrink: true }} value={rec.date} onChange={(e) => setRec({ ...rec, date: e.target.value })} />
                <TextField size="small" label="N° BL du fournisseur" value={rec.bl_fournisseur} onChange={(e) => setRec({ ...rec, bl_fournisseur: e.target.value })} sx={{ flex: 1 }} />
              </Box>
              <Table size="small">
                <TableHead><TableRow><TableCell>Article</TableCell><TableCell align="right">Reste à livrer</TableCell><TableCell sx={{ width: 140 }}>Quantité reçue</TableCell></TableRow></TableHead>
                <TableBody>
                  {bc.lignes.map((l) => {
                    const reste = Math.max(0, l.quantite - l.quantite_recue);
                    return (
                      <TableRow key={l.id}>
                        <TableCell>{l.designation}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{q3(reste)} {l.unite}</TableCell>
                        <TableCell>
                          <TextField size="small" value={rec.q[l.id]} disabled={reste <= 0} inputProps={{ 'aria-label': `Reçu ${l.designation}`, inputMode: 'decimal' }}
                            onChange={(e) => setRec({ ...rec, q: { ...rec.q, [l.id]: e.target.value } })} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TextField fullWidth size="small" label="Observation (écart, état de la marchandise…)" value={rec.commentaire} sx={{ mt: 2 }}
                onChange={(e) => setRec({ ...rec, commentaire: e.target.value })} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Mettez 0 pour un article non livré. Les articles « stock » sont ajoutés au stock des intrants.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRec(null)}>Annuler</Button>
              <Button variant="contained" onClick={saveRec}>Enregistrer la réception</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!motif} onClose={() => setMotif(null)} fullWidth maxWidth="xs">
        {motif && (
          <>
            <DialogTitle>{motif.action === 'annuler' ? 'Annuler le bon de commande' : 'Clôturer le bon de commande'}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <TextField fullWidth autoFocus multiline minRows={2} label={motif.action === 'annuler' ? "Motif de l'annulation" : 'Pourquoi le reliquat est abandonné'}
                value={motif.texte} onChange={(e) => setMotif({ ...motif, texte: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMotif(null)}>Retour</Button>
              <Button variant="contained" color={motif.action === 'annuler' ? 'error' : 'primary'} disabled={!motif.texte.trim()}
                onClick={() => run(() => (motif.action === 'annuler' ? achatsAPI.annuler(id, motif.texte) : achatsAPI.cloturer(id, motif.texte)), () => setMotif(null))}>
                Confirmer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default BonCommande;
